import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/config/firebase';
import {
  EarlyAccessApplication,
  EarlyAccessConfig,
  EarlyAccessStatus,
} from '@/models/early-access.model';

const APPLICATIONS_COLLECTION = 'earlyAccessApplications';
const CONFIG_COLLECTION = 'earlyAccessConfig';
const CONFIG_DOC_ID = 'pilot';

export interface EarlyAccessFilters {
  status?: EarlyAccessStatus | 'all';
  companySize?: string;
  timeline?: string;
  createdFrom?: Date | null;
  createdTo?: Date | null;
}

export class EarlyAccessService {
  static async getConfig(): Promise<EarlyAccessConfig | null> {
    const docRef = doc(db, CONFIG_COLLECTION, CONFIG_DOC_ID);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
      return null;
    }
    const data = snapshot.data();
    return {
      capacity: data.capacity || 25,
      acceptedCount: data.acceptedCount || 0,
      responseDays: data.responseDays || 3,
      lowSpotsThreshold: data.lowSpotsThreshold || 5,
      onboardingCallUrl: data.onboardingCallUrl || null,
    };
  }

  static async getApplications(filters: EarlyAccessFilters): Promise<EarlyAccessApplication[]> {
    const collectionRef = collection(db, APPLICATIONS_COLLECTION);

    const statusFilter = filters.status && filters.status !== 'all' ? filters.status : null;
    const sizeFilter = filters.companySize || null;
    const timelineFilter = filters.timeline || null;

    let queryRef = query(collectionRef, orderBy('createdAt', 'desc'));
    if (statusFilter) {
      queryRef = query(collectionRef, where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
    } else if (sizeFilter) {
      queryRef = query(collectionRef, where('companySize', '==', sizeFilter), orderBy('createdAt', 'desc'));
    } else if (timelineFilter) {
      queryRef = query(collectionRef, where('timeline', '==', timelineFilter), orderBy('createdAt', 'desc'));
    }

    const snapshot = await getDocs(queryRef);
    let applications = snapshot.docs.map((docSnap) =>
      this.docToApplication(docSnap.id, docSnap.data())
    );

    if (statusFilter) {
      applications = applications.filter((app) => app.status === statusFilter);
    }
    if (sizeFilter) {
      applications = applications.filter((app) => app.companySize === sizeFilter);
    }
    if (timelineFilter) {
      applications = applications.filter((app) => app.timeline === timelineFilter);
    }
    if (filters.createdFrom) {
      applications = applications.filter(
        (app) => app.createdAt >= filters.createdFrom!
      );
    }
    if (filters.createdTo) {
      applications = applications.filter(
        (app) => app.createdAt <= filters.createdTo!
      );
    }

    return applications;
  }

  static async reviewApplication(payload: {
    applicationId: string;
    action: 'shortlist' | 'accept' | 'reject';
    notes?: string | null;
  }): Promise<void> {
    const callable = httpsCallable(functions, 'reviewEarlyAccessApplication');
    await callable(payload);
  }

  private static docToApplication(id: string, data: any): EarlyAccessApplication {
    return {
      id,
      companyName: data.companyName || '',
      website: data.website || '',
      industry: data.industry || '',
      companySize: data.companySize || '',
      applicantRole: data.applicantRole || '',
      email: data.email || '',
      phone: data.phone || null,
      benefitsSetup: data.benefitsSetup || '',
      painPoints: data.painPoints || [],
      painPointOther: data.painPointOther || null,
      timeline: data.timeline || '',
      interestReason: data.interestReason || null,
      status: data.status || 'applied',
      referralCode: data.referralCode || '',
      referrerCode: data.referrerCode || null,
      referrerId: data.referrerId || null,
      referrerCompanyName: data.referrerCompanyName || null,
      referralCount: data.referralCount || 0,
      qualifiedReferralCount: data.qualifiedReferralCount || 0,
      score: data.score || 0,
      scoreBreakdown: data.scoreBreakdown || null,
      reviewedBy: data.reviewedBy || null,
      reviewedByName: data.reviewedByName || null,
      reviewedAt: data.reviewedAt?.toDate ? data.reviewedAt.toDate() : null,
      notes: data.notes || null,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
    };
  }
}
